import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, ScanLine, ClipboardCheck, ChefHat } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { useSbAuth } from "@/context/SupabaseAuthContext";
import { supabase } from "@/lib/supabaseClient";
import { create, DEPARTMENTS, KNOWLEDGE_SLUGS, OWNER_ONLY_SLUGS, list } from "@/lib/dataStore";

const TASK_DEPTS = DEPARTMENTS.filter((d) => !KNOWLEDGE_SLUGS.includes(d.slug) && !OWNER_ONLY_SLUGS.includes(d.slug));

export function QuickActionButton() {
  const { currentRestaurant, profile, hasPermission } = useSbAuth();
  const rid = currentRestaurant?.id;
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);

  const canTask = hasPermission("task.create");
  const canFoodcost = hasPermission("foodcost.access");

  const actions = [
    canFoodcost && { key: "faktura", label: "Skanna faktura", icon: ScanLine, run: () => nav("/konto/foodcost", { state: { tab: "fakturor" } }) },
    canTask && { key: "uppgift", label: "Skapa uppgift", icon: ClipboardCheck, run: () => setTaskOpen(true) },
    canFoodcost && { key: "recept", label: "Skapa recept", icon: ChefHat, run: () => nav("/konto/foodcost", { state: { tab: "recept", openNew: true } }) },
  ].filter(Boolean);

  if (!rid || actions.length === 0) return null;

  const pick = (a) => { setOpen(false); a.run(); };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-40 bg-foreground/10 backdrop-blur-[1px]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)} data-testid="fab-backdrop" />
        )}
      </AnimatePresence>

      <div className="fixed bottom-24 right-6 md:bottom-6 z-40 flex flex-col items-end gap-3" data-testid="quick-action">
        <AnimatePresence>
          {open && actions.map((a, i) => (
            <motion.button key={a.key} data-testid={`fab-action-${a.key}`} onClick={() => pick(a)}
              initial={{ opacity: 0, y: 12, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.9 }}
              transition={{ duration: 0.18, delay: (actions.length - 1 - i) * 0.04 }}
              className="flex items-center gap-3 pl-4 pr-2 py-2 rounded-full bg-card border border-border shadow-[0_8px_30px_-8px_rgba(20,20,20,0.25)] hover:-translate-x-0.5 transition-transform">
              <span className="text-sm font-medium whitespace-nowrap">{a.label}</span>
              <span className="h-9 w-9 grid place-items-center rounded-full bg-primary text-primary-foreground shrink-0"><a.icon className="h-4 w-4" /></span>
            </motion.button>
          ))}
        </AnimatePresence>
        <button onClick={() => setOpen((o) => !o)} data-testid="fab-toggle" aria-label="Snabbåtgärder"
          className="h-14 w-14 grid place-items-center rounded-full bg-primary text-primary-foreground shadow-[0_10px_30px_-6px_rgba(20,20,20,0.35)] hover:scale-105 active:scale-95 transition-transform">
          <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.2 }}><Plus className="h-6 w-6" /></motion.span>
        </button>
      </div>

      <TaskDialog open={taskOpen} onOpenChange={setTaskOpen} rid={rid} actor={profile?.full_name || "Ägare"} />
    </>
  );
}

const EMPTY_TASK = { title: "", department: TASK_DEPTS[0]?.slug || "kok", assignee: "", priority: "medium", description: "" };

function TaskDialog({ open, onOpenChange, rid, actor }) {
  const [f, setF] = useState(EMPTY_TASK);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (!open || !rid) return;
    setF(EMPTY_TASK);
    (async () => {
      const { data } = await supabase.from("memberships")
        .select("role, profile:profiles(id, full_name)").eq("restaurant_id", rid);
      const names = (data || []).map((m) => m.profile?.full_name).filter(Boolean);
      // also merge names already used on existing tasks (fallback if RLS limits profiles)
      const existing = list(rid, "tasks").map((t) => t.assignee).filter(Boolean);
      setMembers([...new Set([...names, ...existing])]);
    })();
  }, [open, rid]);

  const save = () => {
    if (!f.title.trim()) return toast.error("Ange en titel");
    create(rid, "tasks", { entity_type: "task", department: f.department, title: f.title.trim(), description: f.description, status: "todo", priority: f.priority, assignee: f.assignee, tags: [] }, actor);
    toast.success(f.assignee ? `Uppgift skapad och tilldelad ${f.assignee}` : "Uppgift skapad");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fab-task-dialog">
        <DialogHeader><DialogTitle className="font-display">Skapa uppgift</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Vad ska göras?</Label>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="T.ex. Beställ rengöringsmedel" data-testid="fab-task-title" autoFocus /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Avdelning</Label>
              <Select value={f.department} onValueChange={(v) => setF({ ...f, department: v })}>
                <SelectTrigger data-testid="fab-task-department"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_DEPTS.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Prioritet</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger data-testid="fab-task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Låg</SelectItem>
                  <SelectItem value="medium">Medel</SelectItem>
                  <SelectItem value="high">Hög</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5"><Label>Ansvarig (tagga person)</Label>
            {members.length > 0 ? (
              <SearchableSelect
                options={[{ value: "", label: "Ingen ansvarig" }, ...members.map((m) => ({ value: m, label: m }))]}
                value={f.assignee} onChange={(v) => setF({ ...f, assignee: v })}
                placeholder="Välj person" searchPlaceholder="Sök person…" emptyText="Ingen person hittad"
                testid="fab-task-assignee" />
            ) : (
              <Input value={f.assignee} onChange={(e) => setF({ ...f, assignee: e.target.value })} placeholder="Namn på ansvarig" data-testid="fab-task-assignee-input" />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="fab-task-save">Skapa uppgift</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
